/**
 * Apple Health Provider
 *
 * iOS-specific implementation for syncing workouts to/from Apple Health.
 * Uses the custom HeartRateWorkoutPlugin native Swift plugin.
 *
 * @see /docs/APPLE_HEALTH_INTEGRATION_PLAN.md
 */

import { Capacitor, registerPlugin } from '@capacitor/core';
import { BaseHealthProvider } from './base-provider';
import { HealthProvider, HealthActivity, PermissionStatus, ActivityType } from '../types';
import { HEALTH_PROVIDER_IDS, HealthKitWorkout } from '../../../types/health';
import {
  getHealthKitActivityType,
  getHealthKitExerciseType,
} from '../constants';
import { Training } from '../../../types/training';
import { Exercise } from '../../../types/exercise';
import { WorkoutSession } from '../../../types/workout-session';

/**
 * Interface for the native HeartRateWorkoutPlugin
 */
interface HeartRateWorkoutPluginInterface {
  // Live workout session methods
  startWorkoutSession(options: { workoutType: string }): Promise<{ status: string; timestamp: number; workoutType: string }>;
  stopWorkoutSession(): Promise<{ status: string; timestamp: number; workoutId: string; duration: number; calories: number }>;
  pauseWorkoutSession(): Promise<{ status: string; timestamp: number }>;
  resumeWorkoutSession(): Promise<{ status: string; timestamp: number }>;
  isWorkoutSessionActive(): Promise<{ isActive: boolean; state: string }>;

  // Historical workout sync methods
  saveWorkout(options: {
    workoutType: string;
    startDate: string;
    endDate: string;
    duration: number;
    calories?: number;
  }): Promise<{ success: boolean; workoutId?: string }>;

  saveWeight(options: {
    weight: number;
    unit: 'kg' | 'lb';
    date: string;
  }): Promise<{ success: boolean }>;

  queryWorkouts(options: {
    startDate: string;
    endDate: string;
    activityTypes?: number[];
    limit?: number;
  }): Promise<{ workouts: HealthKitWorkout[] }>;

  requestAuthorization(): Promise<{ granted: boolean }>;
}

// Register the native plugin
const HeartRateWorkoutPlugin = registerPlugin<HeartRateWorkoutPluginInterface>('HeartRateWorkoutPlugin');

/**
 * Apple Health Provider implementation
 *
 * Handles:
 * - Syncing trainings to Apple Health
 * - Syncing exercises to Apple Health
 * - Syncing workout sessions to Apple Health
 * - Syncing weight to Apple Health
 * - Querying workouts from Apple Health (for import)
 */
export class AppleHealthProvider extends BaseHealthProvider {
  name = HealthProvider.APPLE_HEALTH;

  /** Provider ID for Firebase metadata (matches original Marune app) */
  readonly providerId = HEALTH_PROVIDER_IDS.IOS_HEALTHKIT;

  /**
   * Check if Apple Health is available (iOS only)
   * Verifies the native plugin is properly registered without triggering an auth prompt.
   * Uses isWorkoutSessionActive() as a lightweight probe — it doesn't request permissions.
   */
  async isAvailable(): Promise<boolean> {
    if (Capacitor.getPlatform() !== 'ios') {
      return false;
    }

    try {
      // Use a read-only call to verify the plugin is registered without triggering
      // a HealthKit authorization prompt. isWorkoutSessionActive just reads local state.
      await HeartRateWorkoutPlugin.isWorkoutSessionActive();
      return true;
    } catch (error: any) {
      // If we get UNIMPLEMENTED, the plugin isn't properly registered in Xcode
      if (error?.code === 'UNIMPLEMENTED') {
        this.logError('HeartRateWorkoutPlugin not registered - add plugin files to Xcode project', error);
        return false;
      }
      // Other errors mean the plugin IS working
      return true;
    }
  }

  /**
   * Request HealthKit permissions
   */
  async requestPermissions(): Promise<PermissionStatus> {
    try {
      if (!await this.isAvailable()) {
        return PermissionStatus.DENIED;
      }

      const result = await HeartRateWorkoutPlugin.requestAuthorization();
      this.log('Permission request result', { granted: result.granted });

      return result.granted ? PermissionStatus.AUTHORIZED : PermissionStatus.DENIED;
    } catch (error) {
      this.logError('Permission request failed', error);
      return PermissionStatus.DENIED;
    }
  }

  /**
   * Check current permission status
   * Note: iOS doesn't provide a way to check HealthKit permission status directly,
   * so we try a minimal operation to verify
   */
  async checkPermissions(): Promise<PermissionStatus> {
    try {
      if (!await this.isAvailable()) {
        return PermissionStatus.DENIED;
      }

      // Try to request authorization - this will return immediately if already authorized
      const result = await HeartRateWorkoutPlugin.requestAuthorization();
      return result.granted ? PermissionStatus.AUTHORIZED : PermissionStatus.DENIED;
    } catch (error) {
      this.logError('Permission check failed', error);
      return PermissionStatus.NOT_DETERMINED;
    }
  }

  /**
   * Sync a Training to Apple Health
   *
   * @param training - Training entity from Marune
   * @returns true if sync was successful
   */
  async syncTraining(training: Training): Promise<boolean> {
    try {
      if (!await this.isAvailable()) {
        return false;
      }

      // Get HealthKit activity type from training style
      const workoutType = getHealthKitActivityType(training.trainedStyle);

      // Calculate dates
      const startDate = new Date(training.date);
      const durationSeconds = (training.totalTrainedTime || 0) * 60; // Convert minutes to seconds
      const endDate = new Date(startDate.getTime() + durationSeconds * 1000);

      const result = await HeartRateWorkoutPlugin.saveWorkout({
        workoutType,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        duration: durationSeconds,
        calories: 0, // Original Marune app doesn't track calories
      });

      this.log('Training synced to Apple Health', {
        trainingId: training.id,
        workoutId: result.workoutId,
        type: workoutType,
        duration: durationSeconds,
      });

      return result.success;
    } catch (error) {
      this.logError('Training sync failed', error);
      return false;
    }
  }

  /**
   * Sync an Exercise to Apple Health
   *
   * @param exercise - Exercise entity from Marune
   * @returns true if sync was successful
   */
  async syncExercise(exercise: Exercise): Promise<boolean> {
    try {
      if (!await this.isAvailable()) {
        return false;
      }

      // Get HealthKit activity type from exercise type
      const workoutType = getHealthKitExerciseType(exercise.type);

      // Calculate dates
      const startDate = new Date(exercise.date);
      const durationSeconds = (exercise.duration || 0) * 60; // Convert minutes to seconds
      const endDate = new Date(startDate.getTime() + durationSeconds * 1000);

      const result = await HeartRateWorkoutPlugin.saveWorkout({
        workoutType,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        duration: durationSeconds,
      });

      this.log('Exercise synced to Apple Health', {
        exerciseId: exercise.exerciseKey,
        workoutId: result.workoutId,
        type: workoutType,
        duration: durationSeconds,
      });

      return result.success;
    } catch (error) {
      this.logError('Exercise sync failed', error);
      return false;
    }
  }

  /**
   * Sync a Workout Session to Apple Health
   *
   * @param workout - WorkoutSession entity from Marune
   * @returns true if sync was successful
   */
  async syncWorkoutSession(workout: WorkoutSession): Promise<boolean> {
    try {
      if (!await this.isAvailable()) {
        return false;
      }

      // Calculate dates
      const startDate = new Date(workout.dateCreated);
      const durationSeconds = workout.duration; // Already in seconds
      const endDate = new Date(startDate.getTime() + durationSeconds * 1000);

      const result = await HeartRateWorkoutPlugin.saveWorkout({
        workoutType: 'crossTraining', // Workout sessions → Cross Training
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        duration: durationSeconds,
      });

      this.log('Workout session synced to Apple Health', {
        sessionId: workout.sessionKey,
        workoutId: result.workoutId,
        duration: durationSeconds,
      });

      return result.success;
    } catch (error) {
      this.logError('Workout session sync failed', error);
      return false;
    }
  }

  /**
   * Save weight to Apple Health
   *
   * @param weight - Weight value
   * @param unit - Weight unit ('kg' or 'lb')
   * @param date - Date of measurement
   * @returns true if save was successful
   */
  async saveWeight(weight: number, unit: 'kg' | 'lb', date: Date): Promise<boolean> {
    try {
      if (!await this.isAvailable()) {
        return false;
      }

      const result = await HeartRateWorkoutPlugin.saveWeight({
        weight,
        unit,
        date: date.toISOString(),
      });

      this.log('Weight saved to Apple Health', { weight, unit });

      return result.success;
    } catch (error) {
      this.logError('Weight save failed', error);
      return false;
    }
  }

  /**
   * Query workouts from Apple Health (for importing)
   *
   * @param startDate - Start of date range
   * @param endDate - End of date range
   * @param options - Optional filters
   * @returns Array of HealthKit workouts
   */
  async queryWorkouts(
    startDate: Date,
    endDate: Date,
    options?: {
      activityTypes?: number[];
      limit?: number;
      excludeMaruneWorkouts?: boolean;
    }
  ): Promise<HealthKitWorkout[]> {
    try {
      if (!await this.isAvailable()) {
        return [];
      }

      const result = await HeartRateWorkoutPlugin.queryWorkouts({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        activityTypes: options?.activityTypes,
        limit: options?.limit || 100,
      });

      let workouts = result.workouts || [];

      // Filter out Marune-created workouts if requested
      if (options?.excludeMaruneWorkouts) {
        workouts = workouts.filter(
          (w) => !w.sourceBundleId?.toLowerCase().includes('marune')
        );
      }

      this.log('Queried workouts from Apple Health', { count: workouts.length });

      return workouts;
    } catch (error) {
      this.logError('Workout query failed', error);
      return [];
    }
  }

  // ============================================================================
  // BaseHealthProvider abstract method implementations
  // ============================================================================

  /**
   * Save a generic health activity (required by BaseHealthProvider)
   */
  async saveActivity(activity: HealthActivity): Promise<boolean> {
    try {
      if (!await this.isAvailable()) {
        return false;
      }

      // Map ActivityType enum to HealthKit workout type string
      const workoutTypeMap: Record<ActivityType, string> = {
        [ActivityType.BOXING]: 'boxing',
        [ActivityType.KICKBOXING]: 'kickboxing',
        [ActivityType.WRESTLING]: 'wrestling',
        [ActivityType.MARTIAL_ARTS]: 'martialArts',
        [ActivityType.HIIT]: 'highIntensityIntervalTraining',
        [ActivityType.YOGA]: 'yoga',
        [ActivityType.RUNNING]: 'running',
        [ActivityType.SWIMMING]: 'swimming',
        [ActivityType.CYCLING]: 'cycling',
        [ActivityType.RESISTANCE]: 'functionalStrengthTraining',
        [ActivityType.CROSS_TRAINING]: 'crossTraining',
        [ActivityType.OTHER]: 'other',
      };

      const workoutType = workoutTypeMap[activity.type] || 'other';

      const result = await HeartRateWorkoutPlugin.saveWorkout({
        workoutType,
        startDate: activity.startDate,
        endDate: activity.endDate,
        duration: activity.duration,
        calories: activity.calories,
      });

      return result.success;
    } catch (error) {
      this.logError('Activity save failed', error);
      return false;
    }
  }

  /**
   * Get activities from Apple Health (required by BaseHealthProvider)
   */
  async getActivities(startDate: Date, endDate: Date): Promise<HealthActivity[]> {
    try {
      const workouts = await this.queryWorkouts(startDate, endDate);

      // Convert HealthKit workouts to HealthActivity format
      return workouts.map((workout) => ({
        type: this.mapHealthKitTypeToActivityType(workout.workoutActivityId),
        startDate: workout.startDate,
        endDate: workout.endDate,
        duration: workout.duration,
        calories: workout.totalEnergyBurned,
        distance: workout.totalDistance,
        source: workout.source,
      }));
    } catch (error) {
      this.logError('Failed to get activities', error);
      return [];
    }
  }

  /**
   * Map HealthKit activity type ID to our ActivityType enum
   */
  private mapHealthKitTypeToActivityType(activityId: number): ActivityType {
    const map: Record<number, ActivityType> = {
      16: ActivityType.BOXING,
      17: ActivityType.KICKBOXING,
      47: ActivityType.MARTIAL_ARTS,
      80: ActivityType.WRESTLING,
      37: ActivityType.RUNNING,
      13: ActivityType.CYCLING,
      46: ActivityType.SWIMMING,
      63: ActivityType.HIIT,
      50: ActivityType.YOGA,
      20: ActivityType.RESISTANCE,
      35: ActivityType.RESISTANCE,
      26: ActivityType.CROSS_TRAINING,
    };

    return map[activityId] || ActivityType.OTHER;
  }
}
